import { WITHDRAWAL_PROMO_CODE } from '@inyova/mocks';
import {
  FeatureFlags,
  Customer,
  LoadState,
  Account,
  GrowAccount,
  CustomerAccount,
  PromotionVm,
  Promotions,
  CustomerAccountItem
} from '@inyova/models';
import { isGrowAccount, isInvestAccount } from '@inyova/utils';
import * as dayjs from 'dayjs';
import { cloneDeep } from 'lodash-es';

import { Action, createReducer, createSelector, on } from '@ngrx/store';
import * as AppRootActions from '@app/app.actions';
import * as AOCHActions from '@app/modules/ao-ch/ao-ch.actions';
import * as ProfileActions from '@profile/profile.actions';
import * as StrategyActions from '@strategy/strategy.actions';

import { AccountOpening3AState, AccountOpeningCHState, AccountOpeningCHUIState, AccountOpeningDEState } from '@shared/models/Account';
import { CiOverviewState } from '@shared/models/CrowdInvestor';
import { PromoCampaignItem } from '@shared/models/DigitalPromo';
import { VerificationState } from '@shared/models/IdNow';
import { ABTest, CreatedPromotions, CreatedPromotionsError, ProfileState, ReferralRewards } from '@shared/models/Profile';
import { ReportingState } from '@shared/models/Reporting';
import { Strategy3AState, StrategyCHState, StrategyDEState, StrategyState, StrategyUIState } from '@shared/models/Strategy';
import { ThirdPartyServiceItem } from '@shared/models/ThirdPartService';

import * as GrowActions from './modules/grow/grow-actions';

export const initialState: RootState = {
  reminder: null,
  reminderModalOpened: false,
  promotionsDialogOpen: false,
  customer: {
    total_current_amount: 0,
    app_location: 'ch',
    created_at: '',
    email: '',
    email_confirmed: false,
    enabled_features: [],
    first_name: '',
    id: 0,
    inyova_id: 0,
    interview_widget: false,
    language: '',
    last_name: '',
    lead_source: '',
    otp_module: '',
    phone: '',
    verified_phone: '',
    unverified_phone: '',
    hide_personalisation: false,
    provider: '',
    source_type_id: 0,
    tag_manager: {
      first_time: false,
      origin: ''
    },
    test_version: '',
    is_friend: false,
    is_test: false,
    referral_code: '',
    total_referrals: {
      free_feedays: 0,
      my_referral_customer: '',
      referrallimit: false
    },
    track_id: '',
    uid: '',
    accounts: [],
    temporary_accounts: [],
    has_crowd_investor_account: false,
    superhuman_visible: false,
    inyova_pro: false,
    saxo_bank_onboarding_done: false,
    saxo_data_renewal_expires_at: null,
    has_interest_account: false,
    show_interest_account_banner: false,
    show_greenness_survey: null,
    show_eve2_opt_in: null,
    qualification_step: null
  },
  currentAccountDetail: {
    id: '',
    owner_name: '',
    step_status: 0,
    onboarding_step: 0,
    kind: '3b',
    edit_mode: false,
    enabled_features: [],
    on_hold: false,
    active: false, // account_active;
    created_at: '',
    initial_investment: 0, // amount_first_funding;
    current_amount: 0,
    stock_split: 0,
    currency: '',
    // flags
    customer_journey: {
      ql_flow_completed: false,
      account_data_filled: false,
      account_funded: false,
      account_opened: false,
      compliance_done: false,
      customer_funded: false,
      id_verification_completed: false
    },
    iban: '',
    is_yova_yellow: false,
    show_eve2_explanation_modal: false,
    liberty_account: {
      qr_iban: '',
      reference_number: ''
    },
    deposit_this_year: 0,
    deposit_qr_code: '',
    max_contribution_level: 'small',
    minimum_investment: 0,
    converted_after_deadline: false,
    tutorial_seen: true,
    ql_onboarding_step: 1,
    risk_recheck_visible: false,
    savings_plan_social_proof: null,
    liquidation_only: false,
    has_low_investment_balance: false,
    inyova_fee: 1.2,
    next_strategy_change_on: '',
    strategy_edit_mode_disabled_at: null,
    strategy_edit_mode_enabled_at: null
  },
  switchAccountLoading: {
    show: false,
    message: ''
  },
  sendCodeStatus: {
    status: '',
    date: new Date()
  },
  verifyCodeStatus: {
    status: '',
    date: new Date()
  },
  referrals: {
    loadState: 'INIT',
    promotions: {
      data: [],
      included: [],
      meta: {
        active_rewards: {
          trees_planted: 0,
          months: 0,
          money_monthly: 0
        },
        pending_rewards: {
          months: 0,
          money_overall: 0,
          funding_months: 0,
          funding_money: 0,
          commitment_months: 0,
          commitment_money: 0
        },
        aggregated_money_rewards: {
          customer_count: 0,
          money_value: 0
        },
        referral_rewards: {
          months: 0,
          money: {
            money_monthly: 0,
            months_duration: 0
          }
        },
        referrer_code: ''
      }
    }
  },
  updatePromotionsStatus: {
    data: {
      type: '',
      id: 0,
      attributes: {
        code: ''
      },
      relationships: {
        promotions: {
          links: {
            related: ''
          },
          data: {
            type: '',
            id: 0
          }
        }
      }
    },
    included: []
  },
  thirdPartyServices: [],
  referralRewards: {
    referrer_name: '',
    referral_rewards: {
      months: 0,
      money: {
        money_monthly: 0,
        months_duration: 0
      }
    }
  },
  referralRewardsStatus: '',
  ABTests: [],
  mobilePromoVisible: false,
  isCIAccountActive: false,
  isLanguageChanged: false,
  oddPageActive: false,
  createNewAccountisLoading: false,
  promoCampaigns: [],
  greennessSurveyNotification: null,
  verifyEmail: {
    resendSubmitted: false,
    cooldownUntil: null
  }
};

const rootReducer = createReducer(
  initialState,
  on(AppRootActions.fetchReminderSuccess, AppRootActions.updateReminderSuccess, (state, action) => ({
    ...state,
    reminder: action.reminder,
    reminderModalOpened: false
  })),
  on(AppRootActions.fetchReminderFail, AppRootActions.updateReminderFail, (state) => ({
    ...state,
    reminder: null
  })),
  on(AppRootActions.setSwitchAccountLoading, (state, action) => ({
    ...state,
    switchAccountLoading: {
      show: action.show,
      message: action.message
    }
  })),
  on(AppRootActions.showReminderDialog, (state, action) => ({
    ...state,
    reminderModalOpened: action.reminderModalOpened
  })),
  on(AppRootActions.fetchCustomerSuccess, (state, action) => {
    return {
      ...state,
      customer: action.customer
    };
  }),
  on(GrowActions.createGrowAccountSuccess, (state, action) => {
    const { id, kind, owner_name, step_status, enabled_features, currency } = action.data;
    return {
      ...state,
      customer: {
        ...state.customer,
        temporary_accounts: [
          ...state.customer.temporary_accounts,
          {
            id,
            kind,
            owner_name,
            step_status,
            enabled_features,
            account_currency: currency
          }
        ]
      }
    };
  }),
  on(
    AppRootActions.fetchCustomerAccountDetailSuccess,
    AppRootActions.fetchCustomerSelectActiveAccount,
    StrategyActions.generateStrategySuccess,
    (state, action): RootState => {
      // BE doesn't return liberty account if the user doesn't have it. it's a workaround for initial state
      // I've used cloneDeep because state daata is unmutable
      const currentAccount: Account = cloneDeep(action.customerAccountDetail);
      if (isInvestAccount(currentAccount) && !currentAccount.liberty_account) {
        currentAccount.liberty_account = {
          qr_iban: '',
          reference_number: ''
        };
      }
      return {
        ...state,
        currentAccountDetail: currentAccount
      };
    }
  ),
  on(AppRootActions.resendVerificationEmailSuccess, (state, { cooldownUntil }): RootState => {
    return {
      ...state,
      verifyEmail: {
        resendSubmitted: true,
        cooldownUntil
      }
    };
  }),
  on(AppRootActions.resendVerificationEmailFail, (state): RootState => {
    return {
      ...state,
      verifyEmail: {
        resendSubmitted: false,
        cooldownUntil: null
      }
    };
  }),
  on(AppRootActions.loadResendEmailCooldownSuccess, (state, { cooldownUntil }): RootState => {
    return {
      ...state,
      verifyEmail: {
        ...state.verifyEmail,
        cooldownUntil
      }
    };
  }),
  on(StrategyActions.updateRiskSuccessEve2, (state): RootState => {
    return {
      ...state,
      currentAccountDetail: {
        ...state.currentAccountDetail,
        step_status: 2
      }
    };
  }),
  on(ProfileActions.logout, () => ({ ...initialState })),
  on(AppRootActions.sendVerificationCodeSuccess, (state, action) => ({
    ...state,
    sendCodeStatus: {
      status: action.status,
      date: action.date
    }
  })),
  on(AppRootActions.sendVerificationCodeFail, (state) => ({
    ...state,
    sendCodeStatus: {
      status: 'error',
      date: new Date()
    }
  })),
  on(AppRootActions.verifyVerificationCodeSuccess, (state, action) => ({
    ...state,
    verifyCodeStatus: {
      status: action.status,
      date: action.date
    }
  })),
  on(AppRootActions.verifyVerificationCodeFail, (state) => ({
    ...state,
    verifyCodeStatus: {
      status: 'error',
      date: new Date()
    }
  })),
  on(AppRootActions.verifyCodeStatusReset, (state) => ({
    ...state,
    verifyCodeStatus: {
      status: '',
      date: new Date()
    }
  })),
  on(AppRootActions.referralsPageOpened, AppRootActions.getPromotions, (state, { forced }): RootState => {
    return state.referrals.loadState === 'LOADED' && !forced ? state : { ...state, referrals: { ...state.referrals, loadState: 'LOADING' } };
  }),
  on(
    AppRootActions.getPromotionsSuccess,
    (state, { data }): RootState => ({
      ...state,
      referrals: {
        promotions: data,
        loadState: 'LOADED'
      }
    })
  ),
  on(
    AppRootActions.getPromotionsFail,
    (state): RootState => ({
      ...state,
      referrals: {
        ...initialState.referrals,
        loadState: { error: 'Something went wrong' }
      }
    })
  ),
  on(AppRootActions.showPromotionsDialog, (state, action) => ({
    ...state,
    promotionsDialogOpen: action.show
  })),
  on(
    AppRootActions.updatePromotionsSuccess,
    AppRootActions.updatePromotionsFail,
    (state, action): RootState => ({
      ...state,
      updatePromotionsStatus: {
        ...action.response
      },
      referrals: {
        ...state.referrals,
        loadState: 'INIT'
      }
    })
  ),
  on(AppRootActions.resetPromotionsStatus, (state) => ({
    ...state,
    updatePromotionsStatus: {
      ...initialState.updatePromotionsStatus
    }
  })),
  on(AppRootActions.fetchThirdPartiesSuccess, (state, action) => ({
    ...state,
    thirdPartyServices: [...state.thirdPartyServices, ...action.response]
  })),
  on(AppRootActions.fetchReferralRewardsSuccess, (state, action) => ({
    ...state,
    referralRewards: action.response,
    referralRewardsStatus: 'success'
  })),
  on(AppRootActions.fetchReferralRewardsFail, (state) => ({
    ...state,
    referralRewardsStatus: 'fail'
  })),
  on(AppRootActions.fetchABTestsSuccess, (state, action) => ({
    ...state,
    ABTests: action.data
  })),
  on(AppRootActions.showMobilePromo, (state, action) => ({
    ...state,
    mobilePromoVisible: action.show
  })),
  on(AppRootActions.toggleCIAccountActive, (state, action) => {
    return {
      ...state,
      isCIAccountActive: action.show
    };
  }),
  on(AppRootActions.toggleDepositSuccessDialog, (state, action) => ({
    ...state,
    showSpDepositSuccessDialog: action.show
  })),
  on(AppRootActions.setIsLanguageChange, (state, action) => ({
    ...state,
    isLanguageChanged: action.change
  })),
  on(AppRootActions.toggleNewAccountIsLoading, (state, action) => ({
    ...state,
    createNewAccountisLoading: action.isLoading
  })),
  on(AppRootActions.toggleOddPageActive, (state, { oddPageActive }) => ({ ...state, oddPageActive })),
  on(AppRootActions.getPromoCampaignsSuccess, (state, action) => ({
    ...state,
    promoCampaigns: action.data.data
  })),
  on(AppRootActions.setCampaignParticipationSuccess, (state, action) => ({
    ...state,
    promoCampaigns: action.data
  })),
  on(AppRootActions.submitGreennessSurveySuccess, AppRootActions.submitGreennessSurveyFail, (state, action) => ({
    ...state,
    greennessSurveyNotification: action.success,
    ...(action.success && { customer: { ...state.customer, show_greenness_survey: null } })
  })),
  on(AppRootActions.submitOnboardingGreennessSurveySuccess, (state) => ({
    ...state,
    customer: {
      ...state.customer,
      show_greenness_survey: null
    }
  })),
  on(AppRootActions.hideSubmitGreennessSurveyNotification, (state) => ({
    ...state,
    greennessSurveyNotification: null
  })),
  on(StrategyActions.updateQLOnboardingStepSuccess, (state, action) => ({
    ...state,
    currentAccountDetail: {
      ...state.currentAccountDetail,
      ql_onboarding_step: action.step
    }
  })),
  on(AOCHActions.submitOddSuccess, (state, { firstName, lastName }) => ({
    ...state,
    customer: {
      ...state.customer,
      first_name: firstName,
      last_name: lastName,
      saxo_data_renewal_expires_at: null
    }
  }))
);

export function reducer(state: RootState | undefined, action: Action): RootState {
  return rootReducer(state, action);
}

export interface State {
  accountOpeningDE: AccountOpeningDEState;
  accountOpening3A: AccountOpening3AState;
  accountOpeningCH: AccountOpeningCHState;
  accountOpeningCHUI: AccountOpeningCHUIState;
  profile: ProfileState;
  reporting: ReportingState;
  root: RootState;
  strategy: StrategyState;
  strategy3A: Strategy3AState;
  strategyCH: StrategyCHState;
  strategyDE: StrategyDEState;
  strategyUI: StrategyUIState;
  verification: VerificationState;
  ciOverview: CiOverviewState;
}
export interface RootState {
  currentAccountDetail: Account;
  customer: Customer;
  reminder: Date;
  reminderModalOpened: boolean;
  promotionsDialogOpen: boolean;
  sendCodeStatus: { status: string; date: Date };
  switchAccountLoading: { show: boolean; message: string };
  verifyCodeStatus: { status: string; date: Date };
  referrals: {
    loadState: LoadState;
    promotions: Promotions;
  };
  updatePromotionsStatus: CreatedPromotions | CreatedPromotionsError[];
  thirdPartyServices: ThirdPartyServiceItem[];
  referralRewards: ReferralRewards;
  referralRewardsStatus: string;
  ABTests: ABTest[];
  mobilePromoVisible: boolean;
  isCIAccountActive: boolean;
  isLanguageChanged: boolean;
  oddPageActive: boolean;
  createNewAccountisLoading: boolean;
  promoCampaigns: PromoCampaignItem[];
  greennessSurveyNotification: null | boolean;
  verifyEmail: {
    resendSubmitted: boolean;
    cooldownUntil: null | number;
  };
}

// SELECTORS
export const selectFeature = (state: State): RootState => state.root;
export const selectAccountOpeningFeature = (state: State): AccountOpeningDEState => state.accountOpeningDE;
export const selectCHAccountOpeningFeature = (state: State): AccountOpeningCHState => state.accountOpeningCH;
export const select3AAccountOpeningFeature = (state: State): AccountOpening3AState => state.accountOpening3A;
export const selectProfileFeature = (state: State): ProfileState => state.profile;
export const selectStrategyFeature = (state: State): StrategyState => state.strategy;
export const selectStrategyFeatureCH = (state: State): StrategyCHState => state.strategyCH;
export const selectStrategyFeatureDE = (state: State): StrategyDEState => state.strategyDE;
export const selectFeatureStrategy3A = (state: State): Strategy3AState => state.strategy3A;
export const selectStrategyFeatureUI = (state: State): StrategyUIState => state.strategyUI;
export const selectAOCHFeatureUI = (state: State): AccountOpeningCHUIState => state.accountOpeningCHUI;
export const selectCiOverviewFeature = (state: State): CiOverviewState => state.ciOverview;

export const selectReportingFeature = (state: State): ReportingState => state.reporting;
export const selectVerificationFeature = (state: State): VerificationState => state.verification;

export const selectReminder = createSelector(selectFeature, (state: RootState) => {
  if (!state.reminder) {
    return null;
  }

  return dayjs(state.reminder).toDate().toLocaleDateString(state.customer.language, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true
  });
});

export const showReminderDialog = createSelector(selectFeature, (state: RootState) => state.reminderModalOpened);
export const selectCustomer = createSelector(selectFeature, (state: RootState) => state.customer);
export const selectCurrentAccount = createSelector(selectFeature, (state: RootState) => state.currentAccountDetail);
export const selectCurrentInvestAccount = createSelector(selectFeature, (state: RootState): CustomerAccount | null =>
  isInvestAccount(state.currentAccountDetail) ? state.currentAccountDetail : null
);
export const selectCurrentGrowAccount = createSelector(selectFeature, (state: RootState) =>
  isGrowAccount(state.currentAccountDetail) ? state.currentAccountDetail : null
);
export const selectCurrentAccountEditMode = createSelector(selectCurrentInvestAccount, (currentAccount) => currentAccount?.edit_mode);
export const selectSwitchAccountLoading = createSelector(selectFeature, (state: RootState) => state.switchAccountLoading);

export const selectKidsAccounts = createSelector(selectCustomer, (state: Customer) => {
  return state.accounts.filter((account) => account.kind === 'kid');
});
export const selectGrowAccount = createSelector(selectCustomer, (state: Customer) => {
  return state.temporary_accounts.find((account) => account.kind === 'grow');
});
export const selectCustomerAndCurrentAccount = createSelector(selectCustomer, selectCurrentAccount, (customer, currentAccount) => {
  return { customer, currentAccountDetail: currentAccount };
});
export const selectCustomerAndCurrentInvestAccount = createSelector(selectCustomer, selectCurrentInvestAccount, (customer, currentAccount) => {
  return { customer, currentAccountDetail: currentAccount };
});
export const selectCustomerAndGrowAccount = createSelector(
  selectCustomer,
  selectCurrentGrowAccount,
  (customer, account): { customer: Customer; growAccount: GrowAccount | undefined } => ({
    customer,
    growAccount: account
  })
);

export const selectEmailConfirmed = createSelector(selectCustomer, (customer) => customer.email_confirmed);

export const selectReferrals = createSelector(selectFeature, (state) => state.referrals);
export const selectPromotionsLoadState = createSelector(selectReferrals, (referrals) => referrals.loadState);

export const selectPromotions = createSelector(selectReferrals, (referrals) => referrals.promotions);
export const selectSendCodeStatus = createSelector(selectFeature, (state: RootState) => state.sendCodeStatus);
export const selectVerifyCodeStatus = createSelector(selectFeature, (state: RootState) => state.verifyCodeStatus);

export const showPromotionsDialog = createSelector(selectFeature, (state: RootState) => state.promotionsDialogOpen);
export const selectPromotionsUpdateStatus = createSelector(selectFeature, (state: RootState) => state.updatePromotionsStatus);
export const selectShowMoneyPromotionNote = createSelector(selectCustomerAndCurrentInvestAccount, selectPromotions, (customerData, promotions) => {
  if (customerData.currentAccountDetail?.onboarding_step && promotions.meta && promotions.meta.active_rewards) {
    return Boolean(customerData.currentAccountDetail.kind !== '3a' && promotions.meta.active_rewards.money_monthly);
  }
  return false;
});

export const selectNeonPromotion = createSelector(selectPromotions, (promotions: Promotions) => {
  if (!promotions || !promotions.included || promotions.included.length === 0) {
    return false;
  }
  const neonPromotion = promotions.included.find((promotion) => promotion.attributes.code === 'neon3a');
  if (!neonPromotion) {
    return false;
  }
  const neonValidityDate = promotions.data.find((promotion) => Number(promotion.relationships.promotion.data.id) === Number(neonPromotion.id))
    .attributes.valid_from;
  const dayDifference = dayjs().diff(dayjs(neonValidityDate, 'YYYY-MM-DD'), 'days');
  return dayDifference < 60;
});

export const selectWithdrawalPromotion = createSelector(selectPromotions, (promotions: Promotions) => {
  if (!promotions || !promotions.included || promotions.included.length === 0) {
    return false;
  }
  const notForReferMonthsSum = promotions.included
    .filter((promotion) => promotion.attributes.for_referring === false)
    .reduce((acc, next) => {
      return acc + parseInt(next.attributes.months_duration);
    }, 0);

  if (notForReferMonthsSum > 9) {
    // do not show WDL promo step
    return true;
  }

  return !!promotions.included.find((promotion) => promotion.attributes.code === WITHDRAWAL_PROMO_CODE);
});

export const selectNoActiveRewards = createSelector(selectPromotions, selectNeonPromotion, ({ meta: { active_rewards } }, hasNeonPromotion) => {
  return !(active_rewards.months || active_rewards.money_monthly || hasNeonPromotion);
});

export const selectRewardsCurrency = createSelector(selectCustomerAndCurrentAccount, ({ customer, currentAccountDetail: currentAccount }) => {
  const acc3b: CustomerAccountItem | undefined = customer.temporary_accounts.find((account) => account.kind === '3b');
  if (typeof acc3b !== 'undefined') {
    return acc3b.account_currency;
  }
  return currentAccount.currency;
});

export const selectShowRedeemLink = createSelector(selectCustomer, selectPromotions, (customer, promotions) => {
  if (customer.temporary_accounts.some((account) => account.step_status < 5)) {
    return true;
  }
  return promotions.included && promotions.included.length ? false : 'step_5';
});

export const selectReferralsPageVm = createSelector(
  selectPromotionsLoadState,
  selectCustomer,
  selectRewardsCurrency,
  selectNeonPromotion,
  selectPromotions,
  selectNoActiveRewards,
  selectShowRedeemLink,
  (
    loadState,
    customer,
    currency,
    hasNeonPromotion,
    { meta: { active_rewards, pending_rewards, referral_rewards, aggregated_money_rewards } },
    noActiveRewards,
    showRedeemLink
  ) => {
    return {
      loadState,
      hasNeonPromotion,
      promo: {
        activeMonths: active_rewards.months,
        activeMoneyMonthly: active_rewards.money_monthly,
        people: aggregated_money_rewards.customer_count,
        earnedAmount: aggregated_money_rewards.money_value,
        pendingMoneyOverall: pending_rewards.money_overall,
        pendingMonths: pending_rewards.months,
        referralMoneyMonthsDuration: referral_rewards.money !== null ? referral_rewards.money.months_duration : null,
        referralMoneyMoneyMonthly: referral_rewards.money !== null ? referral_rewards.money.money_monthly : null,
        referralMonths: referral_rewards.months
      },
      currency,
      referralCode: customer.referral_code,
      appLocation: customer.app_location,
      language: customer.language,
      noActiveRewards,
      showRedeemLink
    } as PromotionVm;
  }
);

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const selectThirdPartyServices = (service = '') =>
  createSelector(selectFeature, (state: RootState): ThirdPartyServiceItem[] | ThirdPartyServiceItem => {
    if (!service) {
      return state.thirdPartyServices;
    }

    return state.thirdPartyServices.find((item) => item.attributes.feature_key === service);
  });

// eslint-disable-next-line @typescript-eslint/no-unsafe-return
export const selectReferralRewards = createSelector(selectFeature, (state: RootState) => state.referralRewards);
export const selectReferralRewardStatus = createSelector(selectFeature, (state: RootState) => state.referralRewardsStatus);

export const selectActiveABTest = (experimentID: string) =>
  createSelector(selectFeature, (state: RootState) => {
    const concreteABTest: ABTest | undefined = state.ABTests.find((element: ABTest) => element.attributes.experiment_id === experimentID);

    if (typeof concreteABTest === 'undefined') {
      return false;
    }

    if (concreteABTest?.attributes?.alternative === 'false' || concreteABTest?.attributes?.alternative === 'true') {
      return concreteABTest?.attributes?.alternative !== 'false';
    }

    return concreteABTest?.attributes?.alternative;
  });

export const showMobilePromo = createSelector(selectCustomerAndCurrentInvestAccount, selectFeature, (data, state: RootState) => {
  return state.mobilePromoVisible && !data?.customer?.inyova_pro && data?.currentAccountDetail?.step_status === 5;
});

export const selectIsCIActive = createSelector(selectFeature, (state: RootState) => state.isCIAccountActive);

export const selectSavingsPlanSocialProof = createSelector(selectCurrentInvestAccount, (account) => account?.savings_plan_social_proof);

export const selectLanguageChange = createSelector(selectFeature, (state) => state.isLanguageChanged);

export const selectOddPageActive = createSelector(selectFeature, (state: RootState) => state.oddPageActive);

export const selectCreateNewAccountIsLoading = createSelector(selectFeature, (state: RootState) => state.createNewAccountisLoading);

export const selectPromoCampaigns = createSelector(selectFeature, (state: RootState) => state.promoCampaigns);

export const selectGreennessSurveyNotification = createSelector(selectFeature, (state: RootState) => state.greennessSurveyNotification);

export const selectAccountFeatureFlag = (feature: FeatureFlags) =>
  createSelector(selectCurrentAccount, selectFeature, (currentAccountDetail) => {
    return currentAccountDetail?.enabled_features.includes(feature);
  });

export const selectCustomerFeatureFlag = (flag: FeatureFlags) =>
  createSelector(selectCustomer, (customer) => {
    return customer.enabled_features.includes(flag);
  });

export const selectVerifyEmail = createSelector(selectFeature, (state: RootState) => state.verifyEmail);
export const selectResendSubmitted = createSelector(selectVerifyEmail, ({ resendSubmitted }) => resendSubmitted);
export const selectCooldownUntil = createSelector(selectVerifyEmail, ({ cooldownUntil }) => cooldownUntil);
